import type { MidiOutputDevice } from "./midiPort.js";

/**
 * Состояние доступа к Web MIDI API. Используется настройками веб-приложения
 * (секция «Выход») и как диагностика: если устройство не перечислилось —
 * пользователь сразу видит причину.
 */
export type MidiAccessState =
  | { kind: "idle" }
  | { kind: "granted"; outputs: MidiOutputDevice[] }
  | { kind: "denied" }
  | { kind: "unsupported" }
  | { kind: "not-secure" };

export const isWebMidiSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  typeof navigator.requestMIDIAccess === "function";

/** Web MIDI работает только в secure context (HTTPS или localhost). */
export const isSecureContext = (): boolean => {
  if (typeof window === "undefined") return false;
  if (typeof window.isSecureContext === "boolean") {
    return window.isSecureContext;
  }
  return (
    window.location.protocol === "https:" ||
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1"
  );
};

const toDevice = (out: MIDIOutput): MidiOutputDevice => ({
  id: out.id,
  name: out.name ?? out.id,
  manufacturer: out.manufacturer ?? null,
  port: {
    send: (data: Uint8Array) => {
      // TS lib.dom объявляет `MIDIOutput.send(data: number[], ...)`
      // (устаревшие типы; по спеке принимает Uint8Array). Конвертируем.
      out.send(Array.from(data));
    },
  },
});

/** Перечислить все output-порты текущего `MIDIAccess` (hotplug-статус — на лету). */
export const listMidiOutputs = (access: MIDIAccess): MidiOutputDevice[] => {
  const devices: MidiOutputDevice[] = [];
  access.outputs.forEach((out) => {
    if (out.state === "connected") devices.push(toDevice(out));
  });
  return devices;
};

/**
 * Запросить доступ к Web MIDI API. Должен вызываться по user-gesture
 * (браузер покажет permission prompt). Возвращает состояние доступа +
 * актуальный список output-портов.
 */
export const requestMidiAccess = async (): Promise<MidiAccessState> => {
  if (!isSecureContext()) return { kind: "not-secure" };
  if (!isWebMidiSupported()) return { kind: "unsupported" };
  try {
    const access = await navigator.requestMIDIAccess();
    return { kind: "granted", outputs: listMidiOutputs(access) };
  } catch {
    // Пользователь отклонил запрос или доступ запрещён permission policy.
    return { kind: "denied" };
  }
};

/** Найти устройство по id (fallback — по имени, затем `/fantom/i`, затем первое). */
export const findMidiOutput = (
  outputs: MidiOutputDevice[],
  opts: { id?: string; name?: string }
): MidiOutputDevice | null => {
  if (opts.id) {
    const byId = outputs.find((d) => d.id === opts.id);
    if (byId) return byId;
  }
  const byName = opts.name
    ? outputs.find((d) => d.name === opts.name)
    : null;
  if (byName) return byName;
  const fantom = outputs.find((d) => /fantom/i.test(d.name));
  if (fantom) return fantom;
  return outputs[0] ?? null;
};

/**
 * Разрешить `MidiOutputPort` для сохранённого выбора (id/name). Используется
 * плеерами (`TimelinePlayer`, `PatternMiniPlayer`) в MIDI-режиме: доступ уже
 * выдан в настройках, повторный `requestMidiAccess` не показывает prompt.
 * Возвращает null, если доступ не выдан/устройство недоступно.
 */
export const getMidiOutputPort = async (
  selection: { id?: string; name?: string } | null
): Promise<import("./midiPort.js").MidiOutputPort | null> => {
  if (!selection) return null;
  const state = await requestMidiAccess();
  if (state.kind !== "granted") return null;
  const device = findMidiOutput(state.outputs, selection);
  return device?.port ?? null;
};